import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Lock, Copy, Check, Play, Download, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Loader, EmptyState } from "@/components/common/Loader";
import { FileThumb, FilePreviewDialog, DownloadButton, isVideo } from "@/components/common/Files";
import { NewBadge } from "@/components/common/Badges";
import { useFetch, downloadFile, errMsg } from "@/lib/api";
import { fmtDate, hoursSince } from "@/lib/format";

function CopyCaption({ text, id }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      toast.success("Testo copiato");
      setTimeout(() => setDone(false), 2000);
    } catch (e) {
      toast.error("Non è stato possibile copiare: seleziona il testo e copialo a mano");
    }
  };
  return (
    <button data-testid={`social-copy-caption-${id}`} onClick={copy} className="btn-ghost h-9 px-4">
      {done ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {done ? "Copiato" : "Copia testo"}
    </button>
  );
}

function MediaGroup({ label, files, onOpen, aspect }) {
  if (!files.length) return null;
  return (
    <div>
      <p className="eyebrow mb-3">{label}</p>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {files.map((f) => (
          <div key={f.id} className="shrink-0 w-36 sm:w-44">
            <button onClick={() => onOpen(f)} className="relative block w-full" data-testid={`social-file-${f.id}`} aria-label="Anteprima">
              <FileThumb kind="social" file={f} className={`w-full ${aspect}`} />
              {isVideo(f.content_type) && <span className="absolute inset-0 flex items-center justify-center"><Play className="w-8 h-8 text-white drop-shadow" fill="currentColor" /></span>}
            </button>
            <div className="mt-2"><DownloadButton kind="social" id={f.id} compact testid={`social-download-${f.id}`} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LockedPost({ p }) {
  return (
    <article data-testid={`social-post-locked-${p.id}`} className="lux-card overflow-hidden">
      <div className="relative h-56 sm:h-64 overflow-hidden">
        <div className="absolute inset-0 blur-2xl scale-110" style={{ background: "radial-gradient(circle at 30% 30%, #C9A227 0%, transparent 45%), radial-gradient(circle at 70% 70%, #6E685E 0%, transparent 50%), #EFEAE0" }} />
        <div className="absolute inset-0 bg-ivory/40 backdrop-blur-xl flex flex-col items-center justify-center text-center px-6">
          <Lock className="w-6 h-6" strokeWidth={1.2} />
          <p className="font-serif text-xl mt-3">Riservato ai Centri Official Partner</p>
          <p className="text-xs text-warm-text mt-2 max-w-xs">Vuoi accedere a tutti i contenuti? Parlane con il tuo referente Skin Lovers.</p>
        </div>
      </div>
      <div className="p-5 sm:p-6">
        <p className="eyebrow">{fmtDate(p.published_at)}</p>
        <h2 className="text-2xl font-normal mt-1">{p.title}</h2>
      </div>
    </article>
  );
}

function DownloadAll({ p }) {
  const [busy, setBusy] = useState(null);
  if (p.files.length < 2) return null;
  const go = async () => {
    for (let i = 0; i < p.files.length; i++) {
      setBusy(i + 1);
      try {
        await downloadFile("social", p.files[i].id);
      } catch (e) {
        toast.error(errMsg(e));
      }
      await new Promise((r) => setTimeout(r, 900));
    }
    setBusy(null);
    toast.success("Download completati");
  };
  return (
    <button data-testid={`social-download-all-${p.id}`} onClick={go} disabled={!!busy} className="btn-gold h-11 px-6">
      <Download className="w-4 h-4" /> {busy ? `Scarico ${busy} di ${p.files.length}…` : `Scarica tutto (${p.files.length})`}
    </button>
  );
}

function Post({ p, onOpen }) {
  const posts = p.files.filter((f) => f.kind === "post");
  const stories = p.files.filter((f) => f.kind === "story");
  return (
    <article data-testid={`social-post-${p.id}`} className="lux-card p-5 sm:p-8 space-y-6">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <p className="eyebrow">{fmtDate(p.published_at)}</p>
          {hoursSince(p.published_at) < 24 * 7 && <NewBadge />}
          {p.partner_only && <span className="text-[10px] uppercase tracking-[0.18em] text-gold">Official Partner</span>}
        </div>
        <h2 className="text-2xl sm:text-3xl font-normal mt-2">{p.title}</h2>
        <div className="mt-4"><DownloadAll p={p} /></div>
      </div>
      <MediaGroup label="Post" files={posts} onOpen={onOpen} aspect="aspect-[4/5]" />
      <MediaGroup label="Storia" files={stories} onOpen={onOpen} aspect="aspect-[9/16]" />
      {p.caption && (
        <div className="hairline pt-5">
          <p className="eyebrow mb-3">Caption</p>
          <p data-testid={`social-caption-${p.id}`} className="text-sm leading-relaxed whitespace-pre-wrap bg-ivory-deep border border-warm/40 p-4">{p.caption}</p>
          <div className="mt-3"><CopyCaption text={p.caption} id={p.id} /></div>
        </div>
      )}
    </article>
  );
}

function SinglePost({ id, onOpen }) {
  const { data, loading, error } = useFetch(`/social/${id}`);
  return (
    <div>
      <Link to="/contenuti-social" data-testid="social-back-link" className="btn-link inline-flex items-center gap-1.5 mb-6"><ArrowLeft className="w-3.5 h-3.5" /> Tutti i contenuti</Link>
      {loading ? <Loader /> : error || !data ? (
        <EmptyState title="Contenuto non disponibile" text="Il contenuto non è ancora online oppure è stato rimosso." />
      ) : data.locked ? <LockedPost p={data} /> : <Post p={data} onOpen={onOpen} />}
    </div>
  );
}

export default function Social() {
  const { id } = useParams();
  const [file, setFile] = useState(null);
  if (id) {
    return (
      <>
        <SinglePost id={id} onOpen={setFile} />
        <FilePreviewDialog kind="social" file={file} onClose={() => setFile(null)} />
      </>
    );
  }
  return <SocialList file={file} setFile={setFile} />;
}

function SocialList({ file, setFile }) {
  const { data, loading } = useFetch("/social");
  return (
    <div>
      <PageHeader eyebrow="Contenuti social" title="Pronti da pubblicare" subtitle="Ogni settimana grafiche, video e testi preparati per i social del tuo centro. Scarica, copia la caption e pubblica." />
      {loading ? <Loader /> : !data?.length ? (
        <EmptyState title="Presto qui i primi contenuti" text="Appena pubblicheremo nuove grafiche o video riceverai un'email." />
      ) : (
        <div className="space-y-6" data-testid="social-list">
          {data.map((p) => (p.locked ? <LockedPost key={p.id} p={p} /> : <Post key={p.id} p={p} onOpen={setFile} />))}
        </div>
      )}
      <FilePreviewDialog kind="social" file={file} onClose={() => setFile(null)} />
    </div>
  );
}
